import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import EmptyValue from '../../components/EmptyValue';
import InlineField from '../../components/InlineField';
import TypeaheadSelect from '../../components/TypeaheadSelect';
import {
  Edit,
  Trash2,
  Plus,
  User,
  ExternalLink,
  MapPin,
  Home,
  Truck,
  Linkedin,
  Check,
  X,
} from 'lucide-react';

// The account's details, each one editable in place: click the value, change
// it, Enter saves. Owners are a list, so they keep a small edit mode of their
// own. Addresses have their own modal.

function AccountOverviewTab({
  account,
  canDo,
  handleAddAddress,
  handleEditAddress,
  handleDeleteAddress,
  // One field at a time
  saveField,
  // Owners edit mode
  isEditing,
  formData,
  startEditing,
  cancelEditing,
  saveEditing,
  saving,
  // Reference data for dropdowns
  stages,
  leadSources,
  repFirms,
  users,
  ownershipRoleTypes,
  // Owner management
  addOwner,
  updateOwner,
  removeOwner,
  // Industry
  industryItems,
  // Account hierarchy
  allAccounts = [],
  handleSetParent,
}) {
  const canEdit = canDo('accounts', 'edit');
  const [industryText, setIndustryText] = useState('');
  const [pickingIndustry, setPickingIndustry] = useState(false);
  const parentItems = (allAccounts || []).filter((a) => a.id !== account.id);
  const opts = (list, empty) => [{ value: '', label: empty }, ...(list || []).map((x) => ({ value: String(x.id), label: x.name }))];
  const selectDisplay = (value, list) => { const hit = (list || []).find((x) => String(x.id) === String(value)); return hit ? hit.name : ''; };

  return (
    <div className="detail-grid">
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Account Details</h3>
          {canEdit && <span className="text-muted text-sm">Click a value to change it</span>}
        </div>
        <div className="card-body">
          <div className="detail-row">
            <span className="detail-label">Name</span>
            <span className="detail-value">
              <InlineField value={account.name} onSave={(v) => saveField('name', v)} canEdit={canEdit} label="account name" />
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Stage</span>
            <span className="detail-value">
              <InlineField
                value={account.stage_id ? String(account.stage_id) : ''}
                onSave={(v) => saveField('stage_id', v || null)}
                canEdit={canEdit}
                type="select"
                options={opts(stages, 'No stage')}
                label="stage"
                emptyText="Set stage"
                format={(v) => <span className="badge badge-primary">{selectDisplay(v, stages) || '—'}</span>}
              />
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Industry</span>
            <span className="detail-value">
              {pickingIndustry ? (
                <div style={{ maxWidth: '350px' }}>
                  <TypeaheadSelect
                    id="account-industry"
                    placeholder="Search or type industry…"
                    items={industryItems || []}
                    value={industryText}
                    displayValue={industryText}
                    onChange={async (val, name) => { setIndustryText(name); await saveField('industry', val || name || null); setPickingIndustry(false); }}
                    onAddNew={async () => {
                      const typed = (document.querySelector('#account-industry') || {}).value || '';
                      if (typed.trim()) { await saveField('industry', typed.trim()); }
                      setPickingIndustry(false);
                    }}
                    addNewLabel="Use custom industry"
                  />
                  <button type="button" className="btn-link" onClick={() => setPickingIndustry(false)} style={{ fontSize: 12 }}>Cancel</button>
                </div>
              ) : canEdit ? (
                <button type="button" className="inline-field" onClick={() => { setIndustryText(account.industry || ''); setPickingIndustry(true); }} aria-label="Edit industry" title="Click to edit">
                  <span className="inline-field-value">{account.industry || <span className="inline-field-empty">Click to add</span>}</span>
                </button>
              ) : (account.industry || <EmptyValue />)}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Website</span>
            <span className="detail-value">
              <InlineField
                value={account.website || ''}
                onSave={(v) => saveField('website', v || null)}
                canEdit={canEdit}
                label="website"
                placeholder="https://…"
                emptyText="Add website"
                format={(v) => <a href={v} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{v} <ExternalLink size={14} /></a>}
              />
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Lead Source</span>
            <span className="detail-value">
              <InlineField value={account.lead_source_id ? String(account.lead_source_id) : ''} onSave={(v) => saveField('lead_source_id', v || null)} canEdit={canEdit} type="select" options={opts(leadSources, 'None')} label="lead source" emptyText="Not set" format={(v) => selectDisplay(v, leadSources) || '—'} />
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Rep Firm</span>
            <span className="detail-value">
              <InlineField
                value={account.rep_firm_id ? String(account.rep_firm_id) : ''}
                onSave={(v) => saveField('rep_firm_id', v || null)}
                canEdit={canEdit}
                type="select"
                options={opts(repFirms, 'None')}
                label="rep firm"
                emptyText="Not set"
                format={(v) => <Link to={`/rep-firms/${v}`} onClick={(e) => e.stopPropagation()}>{selectDisplay(v, repFirms) || '—'}</Link>}
              />
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Parent Account</span>
            <span className="detail-value">
              {canEdit && handleSetParent ? (
                <div style={{ maxWidth: '350px' }}>
                  <TypeaheadSelect
                    id="account-parent"
                    placeholder="Search accounts…"
                    items={parentItems}
                    value={account.parent_account_id || ''}
                    displayValue={account.parent_account?.name || ''}
                    onChange={(val) => handleSetParent(val)}
                    showAddNewOption={false}
                  />
                  {account.parent_account_id && (
                    <button type="button" className="btn-link" onClick={() => handleSetParent(null)} style={{ fontSize: '0.8rem', padding: 0, border: 'none', background: 'none', color: 'var(--accent-primary)', cursor: 'pointer', textDecoration: 'underline' }}>
                      Detach from parent
                    </button>
                  )}
                </div>
              ) : (
                account.parent_account
                  ? <Link to={`/accounts/${account.parent_account.id}`}>{account.parent_account.name}</Link>
                  : <EmptyValue label="None" />
              )}
            </span>
          </div>
          {account.subsidiaries?.length > 0 && (
            <div className="detail-row">
              <span className="detail-label">Subsidiaries</span>
              <span className="detail-value">
                {account.subsidiaries.map((sub, i) => (
                  <React.Fragment key={sub.id}>
                    <Link to={`/accounts/${sub.id}`}>{sub.name}</Link>
                    {i < account.subsidiaries.length - 1 ? ', ' : ''}
                  </React.Fragment>
                ))}
              </span>
            </div>
          )}
          <div className="detail-row">
            <span className="detail-label">LinkedIn</span>
            <span className="detail-value">
              <InlineField
                value={account.linkedin_company_url || ''}
                onSave={(v) => saveField('linkedin_company_url', v || null)}
                canEdit={canEdit}
                label="LinkedIn page"
                placeholder="https://linkedin.com/company/…"
                emptyText="Add LinkedIn"
                format={(v) => <a href={v} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}><Linkedin size={14} /> Company Page</a>}
              />
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Notes</span>
            <span className="detail-value">
              <InlineField value={account.notes || ''} onSave={(v) => saveField('notes', v || null)} canEdit={canEdit} multiline label="notes" emptyText="No notes" />
            </span>
          </div>

          {/* Addresses — their own modal, not affected by anything above */}
          <div className="addresses-section">
            <div className="addresses-section-header">
              <h4 className="addresses-section-title">Addresses</h4>
              {canEdit && (
                <button className="btn btn-secondary btn-sm" onClick={handleAddAddress}>
                  <Plus size={14} /> Add Address
                </button>
              )}
            </div>
            {account.addresses?.length > 0 ? (
              <div className="addresses-grid">
                {account.addresses.map((address) => (
                  <div key={address.id} className="address-card">
                    <div className="address-card-header">
                      <div className="address-type-badges">
                        {address.is_headquarters ? (
                          <span className="badge badge-primary"><Home size={12} /> Headquarters</span>
                        ) : (
                          <span className="badge badge-gray"><Truck size={12} /> {address.address_type}</span>
                        )}
                        {address.is_default_shipping && !address.is_headquarters && (
                          <span className="badge badge-success">Default Shipping</span>
                        )}
                      </div>
                      {canEdit && (
                        <div className="address-actions">
                          <button className="btn btn-icon btn-sm" onClick={() => handleEditAddress(address)} title="Edit" aria-label="Edit address"><Edit size={14} /></button>
                          <button className="btn btn-icon btn-sm" onClick={() => handleDeleteAddress(address.id)} title="Delete" aria-label="Delete address"><Trash2 size={14} /></button>
                        </div>
                      )}
                    </div>
                    <div className="address-content">
                      <MapPin size={14} className="address-icon" aria-hidden="true" />
                      <div>
                        {address.address_line1 && <div>{address.address_line1}</div>}
                        {address.address_line2 && <div>{address.address_line2}</div>}
                        <div>{[address.city, address.state, address.zip_code].filter(Boolean).join(', ')}</div>
                        {address.country && <div>{address.country}</div>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-secondary mt-2 mb-0">
                No addresses yet.{' '}
                {canEdit && <button type="button" className="btn-link" onClick={handleAddAddress} style={{ padding: 0, border: 'none', background: 'none', color: 'var(--accent-primary)', cursor: 'pointer', textDecoration: 'underline' }}>Add one</button>}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Owners</h3>
          {canEdit && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {isEditing ? (
                <>
                  <button className="btn btn-primary btn-sm" onClick={saveEditing} disabled={saving}><Check size={16} /> {saving ? 'Saving…' : 'Save'}</button>
                  <button className="btn btn-secondary btn-sm" onClick={cancelEditing} disabled={saving}><X size={16} /> Cancel</button>
                </>
              ) : (
                <button className="btn btn-secondary btn-sm" onClick={startEditing}><Edit size={16} /> Edit owners</button>
              )}
            </div>
          )}
        </div>
        <div className="card-body">
          {isEditing ? (
            <>
              {formData.owners?.map((owner, index) => (
                <div key={index} className="owner-item" style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                  <select className="inline-edit-input" value={owner.user_id || ''} onChange={(e) => updateOwner(index, 'user_id', e.target.value)} style={{ flex: 1 }} aria-label="Owner">
                    <option value="">Select User</option>
                    {users?.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                  <select className="inline-edit-input" value={owner.ownership_role_type_id || ''} onChange={(e) => updateOwner(index, 'ownership_role_type_id', e.target.value)} style={{ flex: 1 }} aria-label="Ownership role">
                    <option value="">Role</option>
                    {ownershipRoleTypes?.map((rt) => <option key={rt.id} value={rt.id}>{rt.name}</option>)}
                  </select>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                    <input type="checkbox" checked={!!owner.is_primary} onChange={(e) => updateOwner(index, 'is_primary', e.target.checked ? 1 : 0)} /> Primary
                  </label>
                  <button className="btn-icon" onClick={() => removeOwner(index)} style={{ color: 'var(--accent-danger, #dc2626)' }} aria-label="Remove owner"><Trash2 size={16} /></button>
                </div>
              ))}
              <button className="btn btn-secondary btn-sm" onClick={addOwner} type="button">
                <Plus size={14} /> Add Owner
              </button>
            </>
          ) : (
            account.owners?.length > 0 ? (
              <div className="owners-list">
                {account.owners.map((owner, index) => (
                  <div key={index} className="owner-item">
                    <div className="owner-avatar"><User size={16} /></div>
                    <div className="owner-info">
                      <div className="owner-name">
                        {owner.user_name}
                        {owner.is_primary && <span className="badge badge-primary ml-2">Primary</span>}
                      </div>
                      <div className="owner-role">{owner.ownership_role_type_name || 'No role'}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-secondary">No owners assigned</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default AccountOverviewTab;
